import { ACTIONS } from "apollo/components/command-center/constants";
import { useCommandCenter } from "apollo/containers/command-center";
import { format, isAfter } from "date-fns";
import { useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import { getValidDate } from "ui/lib/helpers";
import { handleUpdateRequest } from "ui/lib/rockyFetcher";
import { useEditable } from "ui/lib/useEditable";
import useRequest from "ui/lib/useRequest";
import { createContainer } from "unstated-next";

const useContainer = ({ columnId } = { columnId: "" }) => {
  const { setItemsLength, context, getFromContext, setModalWidth } =
    useCommandCenter();

  useEffect(() => {
    setModalWidth(420);
    setItemsLength(-1);

    return () => {
      setModalWidth(640);
    };
  }, []);

  const memberId = useMemo(() => getFromContext("memberId"), [context]);
  const action = useMemo(() => getFromContext("ACTION"), [context]);

  const { data: detailed } = useRequest(
    memberId && `users/detailed?userId=${memberId}`
  );
  const [user] = detailed?.users || [];

  const { request } = handleUpdateRequest();

  // Canceled members enter through the GRANT_TRIAL_PERIOD action and get a
  // fresh, separately-attributed trial period via createTrialPeriod.
  // setUserTrialEnd would also work, but when the old trial ended recently it
  // extends that record (often the INTRO trial) instead of creating a new
  // one, which muddies the trial history. The endpoint choice rides on the
  // ACTION so it's known synchronously — the users/detailed fetch is slow and
  // may not have resolved by the time a date is picked.
  const isGrantingNewTrial = action === ACTIONS.MEMBER.GRANT_TRIAL_PERIOD;

  const updateDate = async (value) => {
    if (!request) return;

    if (isGrantingNewTrial && !isAfter(getValidDate(value), new Date())) {
      toast.error("Trial end date must be in the future", {
        position: "top-center",
      });
      return;
    }

    const updateTrialEnd = isGrantingNewTrial
      ? request(
          "/createTrialPeriod",
          {
            userID: memberId,
            startTSStr: new Date().toISOString(),
            endTSStr: getValidDate(value).toISOString(),
            reason: "ADMIN_GRANT",
          },
          "post"
        )
      : request(
          "/setUserTrialEnd",
          {
            userID: memberId,
            trialEndTSStr: value,
          },
          "post"
        );

    toast.promise(
      updateTrialEnd,
      {
        loading: "Loading...",
        error: "Something went wrong, please try again",
        success: isGrantingNewTrial
          ? `New trial granted through ${format(getValidDate(value), "MMM d, y")}`
          : `Date has been updated to ${format(getValidDate(value), "MMM d, y")}`,
      },
      {
        position: "top-center",
      }
    );
  };

  const {
    error,
    isSaving,
    value: editableValue,
    handleChange,
  } = useEditable(user?.[columnId], updateDate);

  return {
    user,
    value: editableValue,
    updateDate: handleChange,
    isSaving,
    error,
    isGrantingNewTrial,
  };
};

export const {
  useContainer: useCommandCenterMembersDate,
  Provider: CommandCenterMembersDateProvider,
} = createContainer(useContainer);
